import { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';
import brokerWsService from '../services/BrokerWebSocketService';
import { toast } from 'react-hot-toast';

// Normalize position to always have volume_lots and contract_size
function normalizePosition(p) {
  const contractSize = p.contract_size || 1;
  return {
    ...p,
    id: p.position_id ?? p.id,
    contract_size: contractSize,
    volume_lots: p.volume_lots ?? parseFloat(p.volume) / contractSize,
  };
}

const BrokerContext = createContext();

export const useBroker = () => {
  const context = useContext(BrokerContext);
  if (!context) throw new Error('useBroker must be used within BrokerProvider');
  return context;
};

// ─── Helper: remove a position by id from state ───────────────────────────────
function removePosition(prev, positionId) {
  return prev.filter(p => p.id !== positionId && p.position_id !== positionId);
}

// ─── Helper: clear margin alert for an account ───────────────────────────────
function clearMarginAlert(setAccountSnapshots, accountId) {
  setAccountSnapshots(prev => {
    const updated = new Map(prev);
    const snap = updated.get(accountId);
    if (snap) {
      updated.set(accountId, { ...snap, margin_level: null });
    }
    return updated;
  });
}

export const BrokerProvider = ({ children }) => {
  const [broker, setBroker] = useState(null);
  const [brokerInfo, setBrokerInfo] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [brokerLoading, setBrokerLoading] = useState(true);
  const [brokerError, setBrokerError] = useState(null);

  const [realTimePositions, setRealTimePositions] = useState([]);
  const [realTimeOrders, setRealTimeOrders] = useState([]);
  const [accountSnapshots, setAccountSnapshots] = useState(new Map());
  const [wsConnected, setWsConnected] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.realTimePositions = realTimePositions;
      window.realTimeOrders = realTimeOrders;
    }
  }, [realTimePositions, realTimeOrders]);

  const listenersRegistered = useRef(false);

  useEffect(() => { identifyBroker(); }, []);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (typeof window !== 'undefined') window.brokerWsService = brokerWsService;
    if (token && !brokerWsService.getStatus().connected) {
      brokerWsService.connect(token);
    }
  }, []);

  const identifyBroker = async () => {
    try {
      const response = await api.get('/public/broker');
      if (response.data.success && response.data.data) {
        const brokerData = response.data.data;
        setBrokerInfo(brokerData);
        setBrokerError(null);
        localStorage.setItem('brokerInfo', JSON.stringify(brokerData));
        applyBrokerBranding(brokerData);
      } else {
        throw new Error('Invalid broker response');
      }
    } catch (err) {
      const cachedBroker = localStorage.getItem('brokerInfo');
      if (cachedBroker) {
        try {
          const brokerData = JSON.parse(cachedBroker);
          setBrokerInfo(brokerData);
          applyBrokerBranding(brokerData);
        } catch (e) {}
      }
      setBrokerError(err.response?.data?.error || err.message || 'Failed to identify broker');
    } finally {
      setBrokerLoading(false);
    }
  };

  const applyBrokerBranding = (brokerData) => {
    if (brokerData.primary_color) document.documentElement.style.setProperty('--primary-color', brokerData.primary_color);
    if (brokerData.secondary_color) document.documentElement.style.setProperty('--secondary-color', brokerData.secondary_color);
    if (brokerData.name) document.title = `${brokerData.name} Admin Panel`;
    if (brokerData.logo_url) {
      const favicon = document.querySelector("link[rel='icon']");
      if (favicon) favicon.href = brokerData.logo_url;
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) { brokerWsService.disconnect(); return; }
    if (!brokerWsService.getStatus().connected) brokerWsService.connect(token);
  }, [broker]);

  useEffect(() => {
    if (listenersRegistered.current) return;
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    listenersRegistered.current = true;

    const unsubConnected = brokerWsService.on('connected', (status) => {
      setWsConnected(status);
      if (status) {
        toast.success('Real-time monitoring connected');
        brokerWsService.requestBrokerStats();
        brokerWsService.requestExposure();
      } else {
        toast.error('Real-time monitoring disconnected');
      }
    });

    const unsubPositionsUpdate = brokerWsService.on('positions:update', (data) => {
      const positions = Array.isArray(data)
        ? data
        : Array.isArray(data?.positions) ? data.positions : null;

      if (!positions) return;

      setRealTimePositions(positions.map(normalizePosition));

      const symbols = [...new Set(positions.map(p => p.symbol).filter(Boolean))];
      if (symbols.length > 0) brokerWsService.subscribePrices(symbols);
    });

    const unsubPositionOpened = brokerWsService.on('position:opened', (data) => {
      setRealTimePositions(prev => {
        const id = data.position_id ?? data.id;
        if (prev.some(p => p.id === id || p.position_id === id)) return prev;
        const contractSize = data.contract_size || 1;
        return [...prev, normalizePosition({
          ...data,
          id,
          pnl_currency: 0,
          current_price_units: data.open_price_units ?? data.open_price * Math.pow(10, data.price_scale || 5),
          contract_size: contractSize,
          volume_lots: data.volume_lots ?? data.volume / contractSize,
        })];
      });
      brokerWsService.subscribePrices([data.symbol]);
      toast.success(
        `🟢 ${data.account_number || `Account ${data.account_id}`}: ${data.symbol} ${data.side.toUpperCase()}`,
        { duration: 3000 }
      );
    });

    const unsubPositionClosed = brokerWsService.on('position:closed', (data) => {
      const posId = data.position_id ?? data.id;

      setRealTimePositions(prev => {
        const remaining = removePosition(prev, posId);
        if (data.symbol && !remaining.some(p => p.symbol === data.symbol)) {
          brokerWsService.unsubscribePrices([data.symbol]);
        }
        return remaining;
      });

      setRealTimePositions(prev => {
        const accountStillHasPositions = prev.some(p => p.account_id === data.account_id);
        if (!accountStillHasPositions) {
          clearMarginAlert(setAccountSnapshots, data.account_id);
        }
        return prev;
      });

      const pnl = data.net_profit ?? data.pnl ?? 0;
      toast(
        `${pnl >= 0 ? '💚' : '❌'} Position closed: ${data.symbol} | ${pnl >= 0 ? '+' : ''}$${pnl.toFixed(2)}`,
        {
          duration: 4000,
          style: { background: pnl >= 0 ? '#10b981' : '#ef4444', color: '#fff' },
        }
      );
    });

    const unsubPositionModified = brokerWsService.on('position:modified', (data) => {
      const posId = data.position_id ?? data.id;
      setRealTimePositions(prev =>
        prev.map(p =>
          (p.id === posId || p.position_id === posId)
            ? {
                ...p,
                stop_loss: data.stop_loss,
                take_profit: data.take_profit,
                stop_loss_units: data.stop_loss ? data.stop_loss * Math.pow(10, p.price_scale || 5) : null,
                take_profit_units: data.take_profit ? data.take_profit * Math.pow(10, p.price_scale || 5) : null,
              }
            : p
        )
      );
    });

    const unsubSLTriggered = brokerWsService.on('position:sl_triggered', (data) => {
      const posId = data.position_id ?? data.id;
      setRealTimePositions(prev => {
        const remaining = removePosition(prev, posId);
        if (!remaining.some(p => p.account_id === data.account_id)) {
          clearMarginAlert(setAccountSnapshots, data.account_id);
        }
        return remaining;
      });
      toast.error(`🎯 Stop Loss: ${data.symbol} | Loss: $${data.pnl?.toFixed(2)}`, { duration: 5000 });
    });

    const unsubTPTriggered = brokerWsService.on('position:tp_triggered', (data) => {
      const posId = data.position_id ?? data.id;
      setRealTimePositions(prev => {
        const remaining = removePosition(prev, posId);
        if (!remaining.some(p => p.account_id === data.account_id)) {
          clearMarginAlert(setAccountSnapshots, data.account_id);
        }
        return remaining;
      });
      toast.success(`🎯 Take Profit: ${data.symbol} | Profit: $${data.pnl?.toFixed(2)}`, { duration: 5000 });
    });

    const unsubAccountSnapshot = brokerWsService.on('account_snapshot', (snapshot) => {
      setAccountSnapshots(prev => {
        const updated = new Map(prev);
        updated.set(snapshot.account_id, snapshot);
        return updated;
      });

      if (snapshot.positions && Array.isArray(snapshot.positions)) {
        setRealTimePositions(prev =>
          prev.map(pos => {
            if (pos.account_id !== snapshot.account_id) return pos;
            const enginePos = snapshot.positions.find(
              p => p.position_id === pos.position_id || p.position_id === pos.id
            );
            if (!enginePos) return pos;
            return {
              ...pos,
              current_price: enginePos.current_price,
              current_price_units: enginePos.current_price * Math.pow(10, pos.price_scale || 5),
              pnl_currency: enginePos.unrealized_pnl,
              required_margin: enginePos.required_margin,
            };
          })
        );
      }
    });

    const unsubOrderPlaced = brokerWsService.on('order:placed', (data) => {
      setRealTimeOrders(prev => [...prev, { ...data, id: data.order_id }]);
      toast.success(`📋 Order placed: ${data.symbol} ${data.side.toUpperCase()}`, { duration: 3000 });
    });

    const unsubOrderCancelled = brokerWsService.on('order:cancelled', (data) => {
      const oid = data.order_id ?? data.id;
      setRealTimeOrders(prev => prev.filter(o => o.order_id !== oid && o.id !== oid));
      toast.info(`❌ Order cancelled: ${data.symbol || 'Order'}`);
    });

    const unsubOrderFilled = brokerWsService.on('order:filled', (data) => {
      const oid = data.order_id ?? data.id;
      setRealTimeOrders(prev => prev.filter(o => o.order_id !== oid && o.id !== oid));
      toast.success(`✅ Order filled: ${data.symbol || 'Order'}`);
    });

    const unsubOrderModified = brokerWsService.on('order:modified', (data) => {
      const oid = data.order_id ?? data.id;
      setRealTimeOrders(prev =>
        prev.map(o => (o.order_id === oid || o.id === oid) ? { ...o, ...data } : o)
      );
    });

    const unsubMarginWarning = brokerWsService.on('margin:warning', (data) => {
      toast.error(
        `⚠️ MARGIN CALL - Account ${data.account_number || data.account_id}: ${data.margin_level?.toFixed(2)}%`,
        { duration: 10000 }
      );
    });

    const unsubMarginStopOut = brokerWsService.on('margin:stop_out', (data) => {
      const closedIds = data.positions_closed?.map(p => p.position_id ?? p.id) ?? [];

      if (closedIds.length > 0) {
        setRealTimePositions(prev => {
          const remaining = prev.filter(p => !closedIds.includes(p.id) && !closedIds.includes(p.position_id));
          const removedSymbols = prev
            .filter(p => closedIds.includes(p.id) || closedIds.includes(p.position_id))
            .map(p => p.symbol)
            .filter(sym => sym && !remaining.some(r => r.symbol === sym));
          if (removedSymbols.length > 0) brokerWsService.unsubscribePrices(removedSymbols);
          return remaining;
        });
      }

      clearMarginAlert(setAccountSnapshots, data.account_id);

      const posCount = data.positions_closed?.length || 0;
      toast.error(
        `🚨 STOP OUT - Account ${data.account_number || data.account_id}: ${posCount} position${posCount !== 1 ? 's' : ''} closed`,
        {
          duration: 15000,
          style: { background: '#dc2626', color: '#fff', fontWeight: 'bold' },
        }
      );
    });

    const unsubBrokerStats    = brokerWsService.on('broker:stats',    () => {});
    const unsubBrokerExposure = brokerWsService.on('broker:exposure', () => {});
    const unsubBrokerAlert    = brokerWsService.on('broker:alert',    (data) => toast.warning(data.message, { duration: 8000 }));
    const unsubClientLogin    = brokerWsService.on('client:login',    () => {});
    const unsubClientLogout   = brokerWsService.on('client:logout',   () => {});

    return () => {
      listenersRegistered.current = false;
      unsubConnected();
      unsubPositionsUpdate();
      unsubPositionOpened();
      unsubPositionClosed();
      unsubPositionModified();
      unsubSLTriggered();
      unsubTPTriggered();
      unsubAccountSnapshot();
      unsubOrderPlaced();
      unsubOrderCancelled();
      unsubOrderFilled();
      unsubOrderModified();
      unsubMarginWarning();
      unsubMarginStopOut();
      unsubBrokerStats();
      unsubBrokerExposure();
      unsubBrokerAlert();
      unsubClientLogin();
      unsubClientLogout();
    };
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/broker/auth/login', { email, password });
    if (response.data.success) {
      localStorage.setItem('broker_token', response.data.data.token);
      localStorage.setItem('broker_refresh_token', response.data.data.refreshToken);
      setBroker(response.data.data.admin);
    }
    return response.data;
  };

  const logout = async () => {
    await api.post('/broker/auth/logout');
    localStorage.removeItem('broker_token');
    localStorage.removeItem('broker_refresh_token');
    brokerWsService.disconnect();
    setBroker(null);
    setDashboardStats(null);
    setRealTimePositions([]);
    setRealTimeOrders([]);
    setAccountSnapshots(new Map());
  };

  const refreshToken = async (token) => {
    const response = await api.post('/broker/auth/refresh', { refreshToken: token });
    if (response.data.success) localStorage.setItem('broker_token', response.data.data.token);
    return response.data;
  };

  const getProfile = async () => {
    const response = await api.get('/broker/auth/me');
    if (response.data.success) setBroker(response.data.data);
    return response.data;
  };

  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/broker/dashboard/stats');
      if (response.data.success) setDashboardStats(response.data.data);
      return response.data;
    } finally { setLoading(false); }
  };

  const fetchFeedInstruments     = async (f = {}) => { const p = new URLSearchParams(); if (f.category) p.append('category', f.category); if (f.search) p.append('search', f.search); return (await api.get(`/broker/instruments/feed?${p}`)).data; };
  const fetchEnabledInstruments  = async (f = {}) => { const p = new URLSearchParams(); if (f.category) p.append('category', f.category); if (f.active !== undefined) p.append('active', f.active); if (f.search) p.append('search', f.search); return (await api.get(`/broker/instruments/enabled?${p}`)).data; };
  const enableInstrument         = async (symbol, settings = {}) => (await api.post('/broker/instruments/enable', { symbol, settings })).data;
  const disableInstrument        = async (symbol) => (await api.post('/broker/instruments/disable', { symbol })).data;
  const bulkEnableInstruments    = async (symbols, defaultSettings = {}) => (await api.post('/broker/instruments/bulk-enable', { symbols, defaultSettings })).data;
  const fetchSymbolSettings      = async (symbol) => (await api.get(`/broker/instruments/settings?symbol=${encodeURIComponent(symbol)}`)).data;
  const updateSymbolSettings     = async (symbol, settings) => (await api.put(`/broker/instruments/settings?symbol=${encodeURIComponent(symbol)}`, settings)).data;
  const fetchGroupSymbolSettings = async (groupId, f = {}) => { const p = new URLSearchParams(); if (f.symbol) p.append('symbol', f.symbol); return (await api.get(`/broker/groups/${groupId}/symbols?${p}`)).data; };
  const fetchGroupSymbolSetting  = async (groupId, symbol) => (await api.get(`/broker/groups/${groupId}/symbols/${encodeURIComponent(symbol)}`)).data;
  const updateGroupSymbolSettings = async (groupId, symbol, settings) => (await api.put(`/broker/groups/${groupId}/symbols/${encodeURIComponent(symbol)}`, settings)).data;
  const deleteGroupSymbolSettings = async (groupId, symbol) => (await api.delete(`/broker/groups/${groupId}/symbols/${encodeURIComponent(symbol)}`)).data;
  const bulkUpdateGroupSymbolSettings = async (groupId, symbolSettings) => (await api.post(`/broker/groups/${groupId}/symbols/bulk`, { symbolSettings })).data;
  const adminClosePosition       = async (accountId, positionId) => (await api.post(`/broker/positions/${positionId}/close`, { accountId })).data;
  const adminModifyPosition      = async (accountId, positionId, updates) => (await api.put(`/broker/positions/${positionId}`, { accountId, ...updates })).data;
  const adminCancelOrder         = async (accountId, orderId) => (await api.delete(`/broker/orders/${orderId}`, { data: { accountId } })).data;
  const adminModifyOrder         = async (accountId, orderId, updates) => (await api.put(`/broker/orders/${orderId}`, { accountId, ...updates })).data;
  const fetchClients             = async (f = {}) => { const p = new URLSearchParams(); if (f.is_active !== undefined) p.append('is_active', f.is_active); if (f.kyc_status) p.append('kyc_status', f.kyc_status); if (f.search) p.append('search', f.search); if (f.limit) p.append('limit', f.limit); return (await api.get(`/broker/clients?${p}`)).data; };
  const fetchClientDetails       = async (userId) => (await api.get(`/broker/clients/${userId}`)).data;
  const updateClient             = async (userId, updates) => (await api.put(`/broker/clients/${userId}`, updates)).data;
  const fetchAccounts            = async (f = {}) => { const p = new URLSearchParams(); if (f.account_type) p.append('account_type', f.account_type); if (f.is_active !== undefined) p.append('is_active', f.is_active); if (f.user_id) p.append('user_id', f.user_id); if (f.search) p.append('search', f.search); if (f.limit) p.append('limit', f.limit); return (await api.get(`/broker/accounts?${p}`)).data; };
  const fetchAccountDetails      = async (accountId) => (await api.get(`/broker/accounts/${accountId}`)).data;
  const updateAccount            = async (accountId, updates) => (await api.put(`/broker/accounts/${accountId}`, updates)).data;
  const creditAccount            = async (accountId, amount, description) => (await api.post(`/broker/accounts/${accountId}/credit`, { amount, description })).data;
  const debitAccount             = async (accountId, amount, description) => (await api.post(`/broker/accounts/${accountId}/debit`, { amount, description })).data;

  const fetchPositions = async (filters = {}) => {
    const p = new URLSearchParams();
    if (filters.status)     p.append('status', filters.status);
    if (filters.symbol)     p.append('symbol', filters.symbol);
    if (filters.account_id) p.append('account_id', filters.account_id);
    if (filters.user_id)    p.append('user_id', filters.user_id);
    if (filters.side)       p.append('side', filters.side);
    if (filters.limit)      p.append('limit', filters.limit);
    const response = await api.get(`/broker/positions?${p}`);
    if (filters.status === 'open' && response.data.success) {
      const positions = response.data.data.positions || [];
      if (realTimePositions.length === 0 && positions.length > 0) {
        setRealTimePositions(positions.map(normalizePosition));
        const symbols = [...new Set(positions.map(pos => pos.symbol).filter(Boolean))];
        if (symbols.length > 0 && brokerWsService.getStatus().connected) brokerWsService.subscribePrices(symbols);
      }
    }
    return response.data;
  };

  const fetchOrders = async (filters = {}) => {
    const p = new URLSearchParams();
    if (filters.status)     p.append('status', filters.status);
    if (filters.symbol)     p.append('symbol', filters.symbol);
    if (filters.account_id) p.append('account_id', filters.account_id);
    if (filters.limit)      p.append('limit', filters.limit);
    const response = await api.get(`/broker/orders?${p}`);
    if (filters.status === 'pending' && response.data.success) {
      const orders = response.data.data.orders || [];
      if (realTimeOrders.length === 0 && orders.length > 0) setRealTimeOrders(orders);
    }
    return response.data;
  };

  const fetchTradeHistory = async (filters = {}) => {
    const p = new URLSearchParams();
    if (filters.symbol)     p.append('symbol', filters.symbol);
    if (filters.account_id) p.append('account_id', filters.account_id);
    if (filters.user_id)    p.append('user_id', filters.user_id);
    if (filters.from_date)  p.append('from_date', filters.from_date);
    if (filters.to_date)    p.append('to_date', filters.to_date);
    if (filters.limit)      p.append('limit', filters.limit);
    return (await api.get(`/broker/trades?${p}`)).data;
  };

  const fetchExposureReport      = async () => (await api.get('/broker/reports/exposure')).data;
  const fetchClientProfitability = async (f = {}) => { const p = new URLSearchParams(); if (f.from_date) p.append('from_date', f.from_date); if (f.to_date) p.append('to_date', f.to_date); if (f.limit) p.append('limit', f.limit); return (await api.get(`/broker/reports/client-profitability?${p}`)).data; };
  const fetchDailyReport         = async (date = null) => (await api.get(`/broker/reports/daily${date ? `?date=${date}` : ''}`)).data;
  const fetchAccountGroups       = async () => (await api.get('/broker/groups')).data;
  const fetchAccountGroup        = async (groupId) => (await api.get(`/broker/groups/${groupId}`)).data;
  const createAccountGroup       = async (groupData) => (await api.post('/broker/groups', groupData)).data;
  const updateAccountGroup       = async (groupId, updates) => (await api.put(`/broker/groups/${groupId}`, updates)).data;
  const deleteAccountGroup       = async (groupId) => (await api.delete(`/broker/groups/${groupId}`)).data;
  const fetchBrokerSettings      = async () => (await api.get('/broker/settings')).data;
  const updateBrokerSettings     = async (settings) => (await api.put('/broker/settings', settings)).data;

  const fetchUserOverride  = async (userId) => { try { return (await api.get(`/broker/user-overrides/user/${userId}`)).data; } catch (e) { if (e.response?.status === 404) return { success: false, data: null }; throw e; } };
  const fetchUserOverrides = async (f = {}) => { const p = new URLSearchParams(); if (f.user_id) p.append('user_id', f.user_id); if (f.search) p.append('search', f.search); if (f.limit) p.append('limit', f.limit); return (await api.get(`/broker/user-overrides?${p}`)).data; };
  const createUserOverride = async (userId, data) => (await api.post(`/broker/user-overrides/user/${userId}`, data)).data;
  const updateUserOverride = async (userId, data) => (await api.put(`/broker/user-overrides/user/${userId}`, data)).data;
  const deleteUserOverride = async (userId) => (await api.delete(`/broker/user-overrides/user/${userId}`)).data;
  const upsertUserOverride = async (userId, data) => (await api.post(`/broker/user-overrides/user/${userId}/upsert`, data)).data;

  const value = {
    brokerInfo, setBrokerInfo, brokerLoading, brokerError, identifyBroker,
    broker, setBroker,
    dashboardStats, loading,
    realTimePositions, realTimeOrders, accountSnapshots, wsConnected, brokerWsService,
    login, logout, refreshToken, getProfile,
    fetchDashboardStats,
    fetchFeedInstruments, fetchEnabledInstruments, enableInstrument, disableInstrument,
    bulkEnableInstruments, fetchSymbolSettings, updateSymbolSettings,
    fetchGroupSymbolSettings, fetchGroupSymbolSetting, updateGroupSymbolSettings,
    deleteGroupSymbolSettings, bulkUpdateGroupSymbolSettings,
    fetchClients, fetchClientDetails, updateClient,
    fetchAccounts, fetchAccountDetails, updateAccount, creditAccount, debitAccount,
    fetchPositions, fetchOrders, fetchTradeHistory,
    adminClosePosition, adminModifyPosition, adminCancelOrder, adminModifyOrder,
    fetchExposureReport, fetchClientProfitability, fetchDailyReport,
    fetchAccountGroups, fetchAccountGroup, createAccountGroup, updateAccountGroup, deleteAccountGroup,
    fetchBrokerSettings, updateBrokerSettings,
    fetchUserOverride, fetchUserOverrides, createUserOverride, updateUserOverride,
    deleteUserOverride, upsertUserOverride,
  };

  if (brokerLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '1rem' }}>
        <div className="spinner" />
        <p>Identifying broker...</p>
      </div>
    );
  }

  if (brokerError && !brokerInfo) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '1rem', padding: '2rem' }}>
        <h2>⚠️ Broker Not Found</h2>
        <p style={{ color: '#e74c3c' }}>{brokerError}</p>
        <p style={{ fontSize: '0.9rem', color: '#666' }}>Hostname: {window.location.hostname}</p>
        <button onClick={identifyBroker} style={{ padding: '0.5rem 1rem', background: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  return <BrokerContext.Provider value={value}>{children}</BrokerContext.Provider>;
};